import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Center, Checkbox, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel,
  HStack, Input, InputGroup, InputLeftAddon, InputRightAddon, Select, SimpleGrid, Spinner, Switch, Text, VStack, Wrap,
  WrapItem, useToast,
} from "@chakra-ui/react";
import ConfirmDialog from "../ConfirmDialog";
import { getSaudeAnunciosConfig, salvarSaudeAnunciosConfig } from "../../services/api";
import {
  ACCENT, ACCENT_HOVER, ErroCarga, SLUG_MODELO, SeloSomentePausados, contasSomentePausadas, dataHoraBR, iaPausada, num,
  toastDeFalha, usd, useMontado,
} from "./comum";

// Campos que a tela pode alterar (o resto da configuração é só leitura).
const CAMPOS_BOOL = [
  "ativo", "simulacao", "cura_estoque", "cura_reativar_pausado", "cura_fotos", "cura_recriar_categoria", "cura_coleta",
  "cura_recriar_titulo", "cura_atributos", "cura_duplicados", "cura_orfao_sem_estoque", "cura_criar_faltantes",
  "remover_banner", "ia_ativa",
];
const CAMPOS_INT = [
  "varredura_hora", "max_recriacoes_dia", "max_exclusoes_dia", "max_coleta_dia", "max_frete_coleta_pct", "max_correcoes_ciclo",
  "max_tentativas", "espera_verificacao_min", "cooldown_horas",
];
const CAMPOS_USD = ["ia_teto_diario_usd", "ia_reserva_saldo_usd"];
const CAMPOS_MODELO = ["ia_modelo", "ia_modelo_escalonamento"];

const CURAS_CONFIG = [
  { campo: "cura_estoque", titulo: "Repor estoque", ajuda: "Anúncio pausado por falta de estoque no ML, com estoque aqui: repõe a quantidade." },
  { campo: "cura_reativar_pausado", titulo: "Reativar pausados", ajuda: "Pausado pelo vendedor ou pelo ML (inclusive por preço atípico, com o preço atual) e com estoque: reativa." },
  { campo: "cura_fotos", titulo: "Trocar ou ampliar fotos", ajuda: "Anúncio moderado por foto: troca ou amplia as fotos." },
  { campo: "cura_recriar_categoria", titulo: "Corrigir categoria", ajuda: "Categoria errada: troca a categoria ou recria o anúncio na certa." },
  { campo: "cura_coleta", titulo: "Tentar envio por coleta", ajuda: "Ativo com entrega a combinar porque a categoria não tem Mercado Envios: troca por uma categoria com coleta da mesma peça ou de peça parecida (a IA confere) e manda a medida do pacote quando o anúncio não tem. Se o ML não liberar a coleta, volta para a anterior. Desligada, fica como está, sem ir para a Revisão humana." },
  { campo: "cura_recriar_titulo", titulo: "Recriar com título novo", ajuda: "Quando o título é o problema, recria com um título novo." },
  { campo: "cura_atributos", titulo: "Completar atributos", ajuda: "Preenche os atributos que a moderação pede." },
  { campo: "cura_duplicados", titulo: "Excluir duplicados", ajuda: "Encerra e exclui o anúncio repetido do mesmo produto na conta. Também adota o anúncio solto ativo no lugar do moderado (o anterior é excluído)." },
  { campo: "cura_orfao_sem_estoque", titulo: "Pausar anúncios soltos", ajuda: "Anúncio fora do nosso cadastro, de produto sem estoque ou bloqueado: pausa." },
  { campo: "cura_criar_faltantes", titulo: "Criar anúncios que faltam", ajuda: "Produto com estoque e sem anúncio na conta: põe na fila de criação." },
  { campo: "remover_banner", titulo: "Fotos sem o banner TAGA", ajuda: "Não é uma cura: ao trocar ou ampliar fotos, elas sobem sem o banner da TAGA. Desligado, mantém o banner." },
];

const LIMITES_CONFIG = [
  { campo: "max_recriacoes_dia", titulo: "Recriações por dia", sufixo: "por conta", ajuda: "Teto de anúncios recriados por dia em cada conta." },
  {
    campo: "max_exclusoes_dia", titulo: "Exclusões por dia", sufixo: "por conta",
    ajuda: "Duplicados excluídos e anúncios trocados na adoção — exclusão no ML não tem volta.",
  },
  {
    campo: "max_coleta_dia", titulo: "Trocas para coleta por dia", sufixo: "por conta",
    ajuda: "Começa em 100 por dia e sobe para este teto depois de 50 trocas conferidas, se menos de 3% delas forem moderadas. Troca de categoria moderada 2 vezes não é mais usada.",
  },
  {
    campo: "max_frete_coleta_pct", titulo: "Frete grátis máximo", sufixo: "% do preço",
    ajuda: "A partir de R$ 79 o frete grátis da coleta é pago pela TAGA: acima deste custo, o anúncio fica a combinar.",
  },
  { campo: "max_correcoes_ciclo", titulo: "Correções por ciclo", sufixo: "por ciclo", ajuda: "Quantas correções saem em cada ciclo automático." },
  { campo: "max_tentativas", titulo: "Tentativas por anúncio", sufixo: "vezes", ajuda: "Depois disso o anúncio vai para Revisão humana." },
  { campo: "espera_verificacao_min", titulo: "Espera para conferir", sufixo: "min", ajuda: "Tempo até conferir se a correção pegou (o ML reavalia uns 6 min depois)." },
  { campo: "cooldown_horas", titulo: "Espera após recaída", sufixo: "horas", ajuda: "Intervalo entre tentativas quando o anúncio volta a cair." },
];

const OUTRO = "__outro__";

const textoDe = (v) => (v === null || v === undefined ? "" : String(v));
const numeroDe = (v) => {
  const s = String(v ?? "").trim().replace(",", ".");
  return s === "" ? NaN : Number(s);
};

/**
 * Configuração do servidor -> formulário (números como texto para dar para apagar e digitar).
 * As contas em "só pausados" vêm fora da config; sem a lista (servidor antigo), o campo fica de fora.
 */
function paraForm(c, somentePausadas = null) {
  const f = {};
  CAMPOS_BOOL.forEach((k) => { f[k] = !!c[k]; });
  CAMPOS_INT.forEach((k) => { f[k] = textoDe(c[k]); });
  CAMPOS_USD.forEach((k) => { f[k] = c[k] === null || c[k] === undefined ? "" : String(Number(c[k])).replace(".", ","); });
  CAMPOS_MODELO.forEach((k) => { f[k] = textoDe(c[k]); });
  f.contas = (Array.isArray(c.contas) ? c.contas : []).map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (Array.isArray(somentePausadas)) f.contas_somente_pausadas = [...somentePausadas];
  return f;
}

/** Valor no formato que vai para o servidor (e que serve para comparar). */
function valorFinal(campo, v) {
  if (CAMPOS_INT.includes(campo) || CAMPOS_USD.includes(campo)) return numeroDe(v);
  if (CAMPOS_MODELO.includes(campo)) return String(v || "").trim();
  if (campo === "contas" || campo === "contas_somente_pausadas") return Array.isArray(v) ? [...v].sort((a, b) => a - b) : v;
  return v;
}

const CAMPOS = [...CAMPOS_BOOL, ...CAMPOS_INT, ...CAMPOS_USD, ...CAMPOS_MODELO, "contas", "contas_somente_pausadas"];

/** Só o que mudou: o PUT aceita subconjunto, e assim não pisa no que outra pessoa salvou. */
function diferencas(form, original) {
  const d = {};
  CAMPOS.forEach((k) => {
    const a = valorFinal(k, form[k]);
    const b = valorFinal(k, original[k]);
    const igual = Number.isNaN(a) && Number.isNaN(b) ? true : JSON.stringify(a) === JSON.stringify(b);
    if (!igual) d[k] = a;
  });
  return d;
}

function validar(form, mudou, limites) {
  const erros = {};
  const faixa = (k, n) => {
    const l = limites?.[k];
    if (l && (n < l.min || n > l.max)) erros[k] = `Use de ${num(l.min)} a ${num(l.max)}.`;
  };
  CAMPOS_INT.forEach((k) => {
    if (!(k in mudou)) return;
    const t = String(form[k]).trim();
    if (!/^\d+$/.test(t)) erros[k] = "Use um número inteiro.";
    else faixa(k, Number(t));
  });
  CAMPOS_USD.forEach((k) => {
    if (!(k in mudou)) return;
    const t = String(form[k]).trim();
    if (!/^\d+([.,]\d{1,2})?$/.test(t)) erros[k] = "Use um valor como 3,50.";
    else faixa(k, numeroDe(t));
  });
  CAMPOS_MODELO.forEach((k) => {
    if (!(k in mudou)) return;
    if (!SLUG_MODELO.test(String(form[k]).trim())) erros[k] = "Use o id do OpenRouter: fornecedor/modelo (ex.: openai/gpt-5.6-luna).";
  });
  if (!form.contas.length) erros.contas = "Escolha pelo menos uma conta.";
  return erros;
}

function Secao({ titulo, descricao, children }) {
  return (
    <Box as="section" bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" p={{ base: 3, md: 5 }}>
      <Text as="h2" fontWeight="bold" fontSize="md" color="gray.800">{titulo}</Text>
      {descricao && <Text fontSize="xs" color="gray.600" mt={0.5}>{descricao}</Text>}
      <Box mt={3}>{children}</Box>
    </Box>
  );
}

/** Linha inteira clicável (título e ajuda), com no mínimo 44px de altura para o dedo. */
function LinhaSwitch({ id, titulo, ajuda, corAjuda = "gray.600", isChecked, onChange, isDisabled, esquema = "green" }) {
  return (
    <Flex justify="space-between" align="center" gap={4} minH="44px">
      <Box as="label" htmlFor={id} flex="1" minW={0} py={2} cursor={isDisabled ? "default" : "pointer"}>
        <Text id={`${id}-titulo`} fontSize="sm" fontWeight="semibold" color="gray.800">{titulo}</Text>
        {ajuda && <Text id={`${id}-ajuda`} fontSize="xs" color={corAjuda}>{ajuda}</Text>}
      </Box>
      <Switch id={id} colorScheme={esquema} isChecked={isChecked} onChange={(e) => onChange(e.target.checked)} isDisabled={isDisabled}
        aria-labelledby={`${id}-titulo`} aria-describedby={ajuda ? `${id}-ajuda` : undefined} flexShrink={0} />
    </Flex>
  );
}

/** Uma conta por linha (toda a linha liga e desliga); o interruptor "Só pausados" fica à direita. */
function LinhaSomentePausados({ conta, nome, isChecked, onChange, isDisabled }) {
  const id = `saude-cfg-pausados-${conta}`;
  return (
    <Flex justify="space-between" align="center" gap={4} minH="44px">
      <Box as="label" htmlFor={id} flex="1" minW={0} py={2} cursor={isDisabled ? "default" : "pointer"}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">{nome}</Text>
        <Text id={`${id}-ajuda`} fontSize="xs" color={isChecked ? "orange.700" : "gray.600"}>
          {isChecked
            ? "Só pausados: anúncio novo nasce pausado e nada ativa anúncio sozinho, exceto os itens liberados (já venderam sem furo, fora LNG/BL PARTS, estoque ≥ 2): esses nascem ativos e a Saúde cuida só deles."
            : "Liberada: anúncio novo nasce ativo e as rotinas ativam os anúncios com estoque."}
        </Text>
      </Box>
      <HStack spacing={2} flexShrink={0}>
        {/* No celular o texto da linha já diz o modo; o rótulo curto aparece do tablet para cima. */}
        <Text as="label" htmlFor={id} display={{ base: "none", sm: "block" }} fontSize="xs" fontWeight="semibold" color="gray.700"
          cursor={isDisabled ? "default" : "pointer"}>
          Só pausados
        </Text>
        <Switch id={id} colorScheme="orange" isChecked={isChecked} onChange={(e) => onChange(e.target.checked)} isDisabled={isDisabled}
          aria-label={`Só pausados na ${nome}`} aria-describedby={`${id}-ajuda`} />
      </HStack>
    </Flex>
  );
}

function CampoNumero({ id, titulo, ajuda, valor, onChange, erro, limite, sufixo, prefixo, decimal, isDisabled }) {
  return (
    <FormControl isInvalid={!!erro} isDisabled={isDisabled}>
      <FormLabel htmlFor={id} fontSize="sm" mb={1}>{titulo}</FormLabel>
      <InputGroup size={{ base: "md", lg: "sm" }}>
        {prefixo && <InputLeftAddon>{prefixo}</InputLeftAddon>}
        <Input id={id} bg="white" value={valor} onChange={(e) => onChange(e.target.value)}
          {...(decimal
            ? { type: "text", inputMode: "decimal" }
            : { type: "number", inputMode: "numeric", step: 1, min: limite?.min, max: limite?.max })} />
        {sufixo && <InputRightAddon>{sufixo}</InputRightAddon>}
      </InputGroup>
      {erro ? <FormErrorMessage fontSize="xs">{erro}</FormErrorMessage> : (
        <FormHelperText fontSize="xs" color="gray.600">
          {ajuda}{limite ? ` De ${num(limite.min)} a ${num(limite.max)}.` : ""}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function CampoModelo({ id, titulo, ajuda, valor, modelos, onChange, erro, isDisabled }) {
  const sugerido = modelos.find((m) => m.id === valor);
  const [outro, setOutro] = useState(!sugerido && valor !== "");
  const modoOutro = outro || (!sugerido && valor !== "");
  return (
    <FormControl isInvalid={!!erro} isDisabled={isDisabled}>
      <FormLabel htmlFor={id} fontSize="sm" mb={1}>{titulo}</FormLabel>
      <Select id={id} size={{ base: "md", lg: "sm" }} bg="white" value={modoOutro ? OUTRO : valor}
        onChange={(e) => {
          if (e.target.value === OUTRO) {
            setOutro(true);
          } else {
            setOutro(false);
            onChange(e.target.value);
          }
        }}>
        {!valor && !modoOutro && <option value="" disabled>Escolha um modelo</option>}
        {modelos.map((m) => <option key={m.id} value={m.id}>{m.nome || m.id}</option>)}
        <option value={OUTRO}>Outro (digitar o slug)</option>
      </Select>
      {modoOutro && (
        <Input mt={2} size={{ base: "md", lg: "sm" }} bg="white" value={valor} onChange={(e) => onChange(e.target.value)}
          placeholder="fornecedor/modelo" aria-label={`${titulo}: slug do modelo na OpenRouter`}
          autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      )}
      {erro ? <FormErrorMessage fontSize="xs">{erro}</FormErrorMessage> : (
        <FormHelperText fontSize="xs" color="gray.600">
          {sugerido && !modoOutro ? (
            <>
              Entrada {usd(sugerido.preco_entrada, 2)} · saída {usd(sugerido.preco_saida, 2)} por 1M tokens ·{" "}
              {sugerido.visao ? "lê imagem" : "não lê imagem"}
              {sugerido.observacao ? ` · ${sugerido.observacao}` : ""}
              {ajuda ? <><br />{ajuda}</> : null}
            </>
          ) : (
            <>Modelo fora da lista: confira o slug na OpenRouter.{ajuda ? ` ${ajuda}` : ""}</>
          )}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default function ConfigTab({ isAdmin, nomesContas, onSalvo, onSujo }) {
  const toast = useToast();
  const montado = useMontado();
  const [dados, setDados] = useState(null);
  const [estado, setEstado] = useState("carregando");
  const [form, setForm] = useState(null);
  const [geracao, setGeracao] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  // Conta a liberar fica guardada ao fechar: o título não pisca durante a animação de saída.
  const [liberar, setLiberar] = useState({ aberto: false, conta: null });

  const carregar = useCallback(async () => {
    setEstado("carregando");
    const r = await getSaudeAnunciosConfig();
    if (!montado.current) return;
    if (r && r.config) {
      setDados(r);
      setForm(paraForm(r.config, contasSomentePausadas(r)));
      setGeracao((g) => g + 1);
      setEstado("ok");
    } else {
      setEstado("erro");
    }
  }, [montado]);

  useEffect(() => { carregar(); }, [carregar]);

  const original = useMemo(() => (dados?.config ? paraForm(dados.config, contasSomentePausadas(dados)) : null), [dados]);
  const mudou = useMemo(() => (form && original ? diferencas(form, original) : {}), [form, original]);
  const erros = useMemo(() => (form ? validar(form, mudou, dados?.limites) : {}), [form, mudou, dados]);
  const qtdMudou = Object.keys(mudou).length;
  const temErro = Object.keys(erros).length > 0;
  const podeEditar = isAdmin && !salvando;
  const sujo = qtdMudou > 0;

  // A página pergunta antes de trocar de aba com alteração não salva; fechar/recarregar a aba do navegador também avisa.
  useEffect(() => { onSujo?.(sujo); }, [sujo, onSujo]);
  useEffect(() => () => onSujo?.(false), [onSujo]);
  useEffect(() => {
    if (!sujo) return undefined;
    const avisar = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [sujo]);

  // Nunca abre formulário vazio: salvar por cima apagaria a configuração.
  if (estado === "erro") {
    return (
      <Box p={{ base: 3, md: 4 }}>
        <ErroCarga titulo="Não foi possível carregar a configuração." onTentar={carregar}>
          O formulário só abre com a configuração atual carregada, para nada ser salvo por cima sem querer.
        </ErroCarga>
      </Box>
    );
  }
  if (estado === "carregando" || !form) {
    return <Center py={16}><Spinner color={ACCENT} /></Center>;
  }

  const config = dados.config;
  const limites = dados.limites || {};
  const modelos = Array.isArray(dados.modelos_sugeridos) ? dados.modelos_sugeridos : [];
  const contasDisponiveis = Array.isArray(dados.contas_disponiveis) && dados.contas_disponiveis.length
    ? dados.contas_disponiveis
    : form.contas.map((id) => ({ id, nome: `Conta ${id}` }));
  const faixaHora = limites.varredura_hora || { min: 0, max: 23 };
  const horas = [];
  for (let h = Number(faixaHora.min); h <= Number(faixaHora.max); h += 1) horas.push(h);

  const set = (campo) => (valor) => setForm((f) => ({ ...f, [campo]: valor }));
  // Mesmo nome de conta do resto da tela ("Conta 1 · TAGA AUTOPARTES"), também para conta fora do painel.
  const nomeDisponivel = (c) => nomesContas?.[String(c.id)]
    || String(c.nome || `Conta ${c.id}`).replace(/^Conta (\d+) \((.+)\)$/, "Conta $1 · $2");

  const alternarConta = (id) => setForm((f) => ({
    ...f,
    contas: f.contas.includes(id) ? f.contas.filter((c) => c !== id) : [...f.contas, id].sort((a, b) => a - b),
  }));

  // "Só pausados": a seção só aparece se o servidor informa as contas nesse modo.
  const somentePausadas = form.contas_somente_pausadas;
  const temSomentePausadas = Array.isArray(somentePausadas);
  const pausada = (id) => temSomentePausadas && somentePausadas.includes(Number(id));
  const atendidasPausadas = form.contas.filter(pausada);
  // Pôr em "só pausados" não pede confirmação; liberar, sim (as rotinas passam a ativar anúncios).
  const mudarSomentePausados = (id, v) => {
    if (!v) {
      setLiberar({ aberto: true, conta: id });
      return;
    }
    setForm((f) => ({ ...f, contas_somente_pausadas: [...f.contas_somente_pausadas.filter((c) => c !== id), id].sort((a, b) => a - b) }));
  };
  const fecharLiberar = () => setLiberar((l) => ({ ...l, aberto: false }));
  const confirmarLiberar = () => {
    const id = liberar.conta;
    setForm((f) => ({ ...f, contas_somente_pausadas: f.contas_somente_pausadas.filter((c) => c !== id) }));
    fecharLiberar();
  };

  // Ligar o sistema e sair da simulação pedem confirmação; o contrário, não.
  const mudarAtivo = (v) => (v ? setConfirmar("ativo") : setForm((f) => ({ ...f, ativo: false })));
  const mudarSimulacao = (v) => (v ? setForm((f) => ({ ...f, simulacao: true })) : setConfirmar("simulacao"));
  const confirmarMudanca = () => {
    if (confirmar === "ativo") setForm((f) => ({ ...f, ativo: true }));
    if (confirmar === "simulacao") setForm((f) => ({ ...f, simulacao: false }));
    setConfirmar(null);
  };

  const descartar = () => {
    setForm(original);
    setGeracao((g) => g + 1);
  };

  const salvar = async () => {
    if (!qtdMudou || temErro) return;
    setSalvando(true);
    // Ao ligar, manda junto o modo que está na tela (nunca liga escrevendo de verdade por um estado velho);
    // ao sair da simulação, manda junto o liga/desliga que está na tela.
    const corpo = { ...mudou };
    if (corpo.ativo === true && !("simulacao" in corpo)) corpo.simulacao = !!form.simulacao;
    if (corpo.simulacao === false && !("ativo" in corpo)) corpo.ativo = !!form.ativo;
    try {
      const r = await salvarSaudeAnunciosConfig(corpo);
      toast({ title: r?.message || "Configuração salva.", status: "success", duration: 4000, isClosable: true });
      onSalvo?.(r);
      if (!montado.current) return;
      // A resposta traz a lista de "só pausados" atualizada; sem ela (e com a seção na tela), relê tudo.
      const lista = contasSomentePausadas(r);
      if (r?.config && (lista || !temSomentePausadas)) {
        setDados((d) => ({ ...d, config: r.config, ...(lista ? { contas_somente_pausadas: lista } : {}) }));
        setForm(paraForm(r.config, lista));
        setGeracao((g) => g + 1);
      } else {
        carregar();
      }
    } catch (e) {
      toast(toastDeFalha(e, "Não foi possível salvar"));
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  const textoConfirmacao = confirmar === "ativo" ? (
    <VStack align="stretch" spacing={2}>
      <Text>Ligado, o ciclo automático passa a tratar os anúncios com problema, respeitando os limites desta tela.</Text>
      {form.simulacao
        ? <Text color="purple.700">A simulação está ligada: ele só registra o que faria, nada muda no Mercado Livre.</Text>
        : <Text color="red.600" fontWeight="semibold">A simulação está desligada: as correções mudam anúncios de verdade no Mercado Livre.</Text>}
      <Text fontSize="xs" color="gray.600">Vale depois de salvar.</Text>
    </VStack>
  ) : (
    <VStack align="stretch" spacing={2}>
      <Text>
        Fora da simulação, as correções mudam anúncios de verdade no Mercado Livre: reativar, trocar fotos, trocar a categoria para ter
        coleta, recriar e excluir duplicados.
      </Text>
      {!form.ativo && (
        <Text color="orange.700">
          O ciclo automático está desligado e não corrige nada sozinho, mas “Corrigir agora” em um anúncio passa a mudar o anúncio de verdade.
        </Text>
      )}
      <Text fontSize="xs" color="gray.600">Vale depois de salvar.</Text>
    </VStack>
  );

  return (
    <Box p={{ base: 3, md: 4 }}>
      <VStack align="stretch" spacing={4} maxW="860px">
        {!isAdmin && (
          <Alert status="info" borderRadius="md" fontSize="sm"><AlertIcon />Só administradores alteram estas configurações.</Alert>
        )}

        <Secao titulo="Geral">
          <LinhaSwitch id="saude-cfg-ativo" titulo="Sistema ligado" isChecked={form.ativo} onChange={mudarAtivo} isDisabled={!podeEditar}
            ajuda={form.ativo ? "O ciclo automático trata os anúncios com problema." : "Desligado, o ciclo automático não corrige nada."}
            corAjuda={form.ativo ? "gray.600" : "orange.700"} />
          <LinhaSwitch id="saude-cfg-simulacao" titulo="Modo simulação" esquema="purple" isChecked={form.simulacao}
            onChange={mudarSimulacao} isDisabled={!podeEditar}
            ajuda={form.simulacao ? "Só decide e registra o que faria, sem mudar nada no Mercado Livre." : "Muda anúncios de verdade no Mercado Livre."}
            corAjuda={form.simulacao ? "gray.600" : "red.600"} />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={3}>
            <Box as="fieldset" minW={0}>
              <Text as="legend" fontSize="sm" fontWeight="medium" mb={1}>Contas atendidas</Text>
              <Wrap spacing={{ base: 0, md: 4 }}>
                {contasDisponiveis.map((c) => (
                  <WrapItem key={c.id} w={{ base: "100%", md: "auto" }}>
                    <Checkbox size="lg" minH="44px" w="100%" isChecked={form.contas.includes(Number(c.id))}
                      onChange={() => alternarConta(Number(c.id))} isDisabled={!podeEditar} isInvalid={!!erros.contas}>
                      <Text as="span" fontSize="sm">{nomeDisponivel(c)}</Text>
                      {pausada(c.id) && <SeloSomentePausados ml={2} verticalAlign="middle" />}
                    </Checkbox>
                  </WrapItem>
                ))}
              </Wrap>
              {erros.contas && <Text fontSize="xs" color="red.600" mt={1} role="alert">{erros.contas}</Text>}
              {atendidasPausadas.length > 0 && (
                <Text fontSize="xs" color="orange.700" mt={1}>
                  {atendidasPausadas.length === 1
                    ? `Conta ${atendidasPausadas[0]} em “só pausados”: a Saúde atua só nos itens liberados (logo abaixo).`
                    : `Contas ${atendidasPausadas.slice(0, -1).join(", ")} e ${atendidasPausadas[atendidasPausadas.length - 1]} em “só pausados”: a Saúde atua só nos itens liberados (logo abaixo).`}
                </Text>
              )}
            </Box>
            <FormControl isDisabled={!podeEditar}>
              <FormLabel htmlFor="saude-cfg-hora" fontSize="sm" mb={1}>Hora da varredura diária</FormLabel>
              <Select id="saude-cfg-hora" size={{ base: "md", lg: "sm" }} bg="white" value={form.varredura_hora}
                onChange={(e) => set("varredura_hora")(e.target.value)}>
                {form.varredura_hora !== "" && !horas.includes(Number(form.varredura_hora)) && (
                  <option value={form.varredura_hora}>{`${String(form.varredura_hora).padStart(2, "0")}:00`}</option>
                )}
                {horas.map((h) => <option key={h} value={String(h)}>{`${String(h).padStart(2, "0")}:00`}</option>)}
              </Select>
              <FormHelperText fontSize="xs" color="gray.600">Lê todos os anúncios das contas e refaz o diagnóstico.</FormHelperText>
            </FormControl>
          </SimpleGrid>
        </Secao>

        {temSomentePausadas && (
          <Secao titulo="Contas só com anúncios pausados"
            descricao="Vale para o sistema todo, não só para esta tela. Na conta marcada, o anúncio novo nasce pausado e nenhuma rotina automática ativa anúncio dela (fornecedores, estoque), exceto os itens liberados: nascem ativos e a Saúde cuida só deles.">
            <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
              {contasDisponiveis.map((c) => (
                <LinhaSomentePausados key={c.id} conta={Number(c.id)} nome={nomeDisponivel(c)} isChecked={pausada(c.id)}
                  onChange={(v) => mudarSomentePausados(Number(c.id), v)} isDisabled={!podeEditar} />
              ))}
            </VStack>
          </Secao>
        )}

        <Secao titulo="Curas" descricao="O que o sistema pode fazer sozinho. Cura desligada: o anúncio vai para Revisão humana e o sistema não mexe nele.">
          <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
            {CURAS_CONFIG.map((c) => (
              <LinhaSwitch key={c.campo} id={`saude-cfg-${c.campo}`} titulo={c.titulo} ajuda={c.ajuda}
                isChecked={form[c.campo]} onChange={set(c.campo)} isDisabled={!podeEditar} />
            ))}
          </VStack>
        </Secao>

        <Secao titulo="Limites" descricao="Travas para o sistema não exagerar num dia.">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {LIMITES_CONFIG.map((l) => (
              <CampoNumero key={l.campo} id={`saude-cfg-${l.campo}`} titulo={l.titulo} ajuda={l.ajuda} sufixo={l.sufixo}
                valor={form[l.campo]} onChange={set(l.campo)} erro={erros[l.campo]} limite={limites[l.campo]} isDisabled={!podeEditar} />
            ))}
          </SimpleGrid>
        </Secao>

        <Secao titulo="Inteligência artificial" descricao="Usada pela OpenRouter quando precisa decidir categoria, título, foto ou atributo.">
          <LinhaSwitch id="saude-cfg-ia_ativa" titulo="Usar IA" isChecked={form.ia_ativa} onChange={set("ia_ativa")} isDisabled={!podeEditar}
            ajuda={form.ia_ativa ? "Liga as decisões por IA." : "Desligada, nada é decidido por IA."} />
          {iaPausada(config) && (
            <Alert status="warning" borderRadius="md" fontSize="sm" my={2}>
              <AlertIcon />IA pausada até {dataHoraBR(config.ia_pausada_ate, { ano: true })} (sem saldo). Dá para retomar pelo Painel.
            </Alert>
          )}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={3}>
            <CampoModelo key={`m1-${geracao}`} id="saude-cfg-ia_modelo" titulo="Modelo principal" valor={form.ia_modelo} modelos={modelos}
              onChange={set("ia_modelo")} erro={erros.ia_modelo} isDisabled={!podeEditar} />
            <CampoModelo key={`m2-${geracao}`} id="saude-cfg-ia_modelo_escalonamento" titulo="Modelo de escalonamento"
              ajuda="Usado da 2ª tentativa em diante." valor={form.ia_modelo_escalonamento} modelos={modelos}
              onChange={set("ia_modelo_escalonamento")} erro={erros.ia_modelo_escalonamento} isDisabled={!podeEditar} />
            <CampoNumero id="saude-cfg-ia_teto_diario_usd" titulo="Teto de gasto por dia" prefixo="US$" decimal
              ajuda="Gasto máximo de IA por dia." valor={form.ia_teto_diario_usd} onChange={set("ia_teto_diario_usd")}
              erro={erros.ia_teto_diario_usd} limite={limites.ia_teto_diario_usd} isDisabled={!podeEditar} />
            <CampoNumero id="saude-cfg-ia_reserva_saldo_usd" titulo="Reserva de saldo" prefixo="US$" decimal
              ajuda="Não gasta se o saldo da OpenRouter cair abaixo disso." valor={form.ia_reserva_saldo_usd}
              onChange={set("ia_reserva_saldo_usd")} erro={erros.ia_reserva_saldo_usd} limite={limites.ia_reserva_saldo_usd}
              isDisabled={!podeEditar} />
          </SimpleGrid>
        </Secao>

        {(config.atualizado_por_nome || config.updated_at) && (
          <Text fontSize="xs" color="gray.600">
            Última alteração{config.atualizado_por_nome ? ` por ${config.atualizado_por_nome}` : ""}
            {config.updated_at ? ` em ${dataHoraBR(config.updated_at, { ano: true })}` : ""}.
          </Text>
        )}
      </VStack>

      {isAdmin && (
        <Flex position="sticky" bottom={0} zIndex={2} bg="white" borderTopWidth="1px" borderColor="gray.200"
          mx={{ base: -3, md: -4 }} mt={4} px={{ base: 3, md: 4 }} py={3} gap={2} align="center" justify="space-between" flexWrap="wrap"
          boxShadow={qtdMudou ? "0 -4px 12px rgba(0, 0, 0, 0.06)" : "none"} borderBottomRadius="lg">
          <Text fontSize="sm" color={temErro ? "red.600" : qtdMudou ? "orange.700" : "gray.600"} aria-live="polite">
            {temErro
              ? (Object.keys(erros).length === 1 && erros.contas ? erros.contas : "Corrija os campos marcados.")
              : qtdMudou
                ? `${qtdMudou} ${qtdMudou === 1 ? "alteração" : "alterações"} sem salvar.`
                : "Nada para salvar."}
          </Text>
          <HStack spacing={2} flex={{ base: "1 1 100%", sm: "0 0 auto" }} justify="flex-end">
            {qtdMudou > 0 && (
              <Button size={{ base: "md", lg: "sm" }} variant="ghost" onClick={descartar} isDisabled={salvando} flex={{ base: 1, sm: "none" }}>
                Descartar
              </Button>
            )}
            <Button size={{ base: "md", lg: "sm" }} bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} onClick={salvar}
              isLoading={salvando} loadingText="Salvando" isDisabled={!qtdMudou || temErro} flex={{ base: 1, sm: "none" }}>
              Salvar
            </Button>
          </HStack>
        </Flex>
      )}

      <ConfirmDialog
        isOpen={!!confirmar}
        onClose={() => setConfirmar(null)}
        onConfirm={confirmarMudanca}
        title={confirmar === "ativo" ? "Ligar a Saúde dos Anúncios?" : "Sair da simulação?"}
        confirmText={confirmar === "ativo" ? "Ligar" : "Sair da simulação"}
        confirmColor={confirmar === "ativo" ? "green" : "red"}
        buttonSize={{ base: "md", lg: "sm" }}
        wrapButtons
        body={textoConfirmacao}
      />

      <ConfirmDialog
        isOpen={liberar.aberto}
        onClose={fecharLiberar}
        onConfirm={confirmarLiberar}
        title={`Liberar a conta ${liberar.conta ?? ""}?`}
        confirmText="Liberar"
        confirmColor="green"
        buttonSize={{ base: "md", lg: "sm" }}
        wrapButtons
        body={(
          <VStack align="stretch" spacing={2}>
            <Text>
              Os anúncios novos passam a nascer ativos e as rotinas (fornecedores, estoque e a Saúde, se a conta estiver marcada)
              passam a ativar os anúncios com estoque.
            </Text>
            <Text fontSize="xs" color="gray.600">Vale depois de salvar.</Text>
          </VStack>
        )}
      />
    </Box>
  );
}
